import 'libs';
import load from 'little-loader';
import ConfigurationManager from './utils/ConfigurationManager';
import EventManager from './utils/EventManager';
import { loadCSS } from 'fg-loadcss';

/**
 * @class FeatureLoader
 * @description Load features
 */
class RcsOrchestrator {
  constructor(config) {
    this.confMgr = new ConfigurationManager(config);
    this.loadedScripts = {};
    this.eventManager = new EventManager(config.listeners);
  }

  load() {
    let components = this.confMgr.getComponents();
    const featuresByPriority = this.groupBy(components, 'priority');
    Object.keys(featuresByPriority)
      .reduce((features, priority) => {
        return features.then(() =>
          this.loadFeatures(featuresByPriority[priority].filter(o => !o.events))
        );
      }, Promise.resolve())
      .then(() => console.log('Loading features completed'));
    this.loadFeatures(components.filter(o => 'events' in o));
  }

  groupBy(xs, key) {
    return xs.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  }

  /**
   * Load specified feature list in parallel
   * @param {Array} featuresConf
   */
  loadFeatures(featuresConf) {
    return new Promise((resolve, reject) => {
      Promise.all(
        featuresConf.map(featureConf => {
          if (featureConf.events) {
            featureConf.events.forEach(event => {
              let self = this;
              if (Array.from(event.target).length > 0) {
                Array.from(event.target).forEach(target =>
                  this.toggleEventsListener(self, event.eventName, target, featureConf)
                );
              } else {
                this.toggleEventsListener(
                  self,
                  event.eventName,
                  event.target,
                  featureConf
                );
              }
            });
          } else {
            return this.loadFeature(featureConf);
          }
          return 0;
        })
      )
        .then(() => resolve())
        .catch(() => {
          reject();
        });
    });
  }

  toggleEventsListener(self, eventName, target, featureConf) {
    if (target && target.addEventListener) {
      target.addEventListener(
        eventName,
        function handleEvents() {
          self.loadFeature(featureConf);
          target.removeEventListener(eventName, handleEvents, true);
        },
        true
      );
    }
  }

  /**
   * Load specified feature
   * @param {Array} featuresConf
   */
  loadFeature(featureConf) {
    const firstLetter = featureConf.type.charAt(0).toUpperCase();
    const restWord = featureConf.type.substring(1, featureConf.type.length);
    const featureName = `load${firstLetter}${restWord}`;
    return this[featureName](featureConf);
  }

  /**
   * Load style module via async call
   * @param {Object} featureConf
   */
  loadStyle(featureConf) {
    if (featureConf.src.match(/^(https?)|\/\/.*/)) {
      loadCSS(featureConf.src);
      return Promise.resolve('success');
    }
    return new Promise((resolve, reject) => {
      import(`./scss/${featureConf.src}.scss`)
        .then(() => resolve(featureConf))
        .catch(error => {
          console.error('This is an error', error);
          reject(error);
        });
    });
  }

  /**
   * Load internal module via async call
   * @param {Object} featureConf
   */
  loadModule(featureConf) {
    return new Promise((resolve, reject) => {
      import(`./features/${featureConf.src}/feature`)
        .then(({ default: Feature }) => {
          let feature = new Feature();
          if (feature.load) {
            feature.load();
            resolve(featureConf);
          }
        })
        .catch(error => {
          console.error('This is an error', error);
          reject(error);
        });
    });
  }

  /**
   * Load external script via async call
   * @param {Object} featureConf
   */
  loadExt(featureConf) {
    return new Promise(resolve => {
      if (this.loadedScripts && this.loadedScripts[featureConf.src]) {
        console.log(`${featureConf.src} already loaded`);
        return 0;
      }

      load(featureConf.src, {
        callback: err => {
          if (featureConf.callback) {
            featureConf.callback(err);
          }
          this.loadedScripts[featureConf.src] = true;
          resolve(featureConf);
        },
        context: this
      });
    });
  }
}

module.exports = RcsOrchestrator;



// WEBPACK FOOTER //
// ./src/rcs-orchestrator/rcs-orchestrator.component.js